import { apiFetch } from './apiClient.js';

/**
 * Cliente de API tipado y compartido por la familia de apps.
 *
 * `apiFetch` resuelve base URL + credenciales + Bearer nativo, pero cada
 * wrapper de API venía redefiniendo encima suyo lo mismo: una
 * `class XApiError`, un `interface ApiEnvelope<T>` y un `jsonFetch`/`unwrap`
 * (17 copias en el monorepo, 11 sólo en speak). Esta fábrica centraliza esa
 * capa: **una** clase de error por cliente (con `name` propio, para que el
 * `instanceof` de los call sites siga discriminando), el join con el
 * `basePath`, la inyección del `content-type`, el mapeo del fallo de red y el
 * desenvuelto del envelope `{ success, data }`.
 *
 * Lo que NO decide por vos (y por eso son piezas separadas): los wrappers que
 * necesitan otro desenvuelto —`body.data` estricto, JSON crudo, un blob—
 * componen sobre `request` + `ensureOk` en vez de usar `get`/`send`.
 */

/**
 * Base de los errores tipados de la familia. **No se instancia directo**: cada
 * cliente recibe su subclase con `name` propio vía `createTypedApiClient`.
 *
 * - `status` — HTTP del backend, o `0` si la request nunca salió (red).
 * - `code` — `error` crudo del body, `'network_error'` si no salió, o
 *   `'unknown'` si el body del error no traía `error`.
 * - `details` — body crudo del error (p. ej. `{ retryAt }` del cooldown del
 *   self-set de nivel). `{}` cuando no hubo body parseable.
 */
export class TypedApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code: string,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'TypedApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Constructor de la subclase de error que devuelve cada cliente. */
export type TypedApiErrorClass = new (
  message: string,
  status: number,
  code: string,
  details?: Record<string, unknown>,
) => TypedApiError;

export interface TypedApiClientOptions {
  /** Prefijo de todas las rutas (`'/api/modules/english'`). `''` si el wrapper pasa rutas absolutas. */
  readonly basePath: string;
  /** `err.name` de la subclase (`'VocabApiError'`). Es lo que leen los logs y los `toMatchObject` de los tests. */
  readonly errorName: string;
  /**
   * Qué hacer con un `AbortError` (el caller canceló con un `AbortSignal`):
   *
   * - `'rethrow'` — se re-lanza tal cual; el caller distingue "cancelé yo" de
   *   "se cayó la red".
   * - `'wrap'` — se envuelve como `network_error` (o sea el caller ve
   *   "No pudimos conectar con el servidor.").
   *
   * **Sin default a propósito**: los dos comportamientos conviven hoy en speak
   * y elegir uno en silencio cambiaría el copy que ve el user en la mitad de
   * los clientes. Cada wrapper declara el suyo.
   */
  readonly abortError: 'rethrow' | 'wrap';
  /**
   * Subclase propia de `TypedApiError`, para los wrappers que suben campos del
   * body del error a la superficie (listen: `available` del 404 de rendition,
   * `balanceNano`/`priceNano` del 402). El cliente construye **esta** clase en
   * todos sus caminos —incluido el fallo de red—, así que el
   * `instanceof XApiError` de los call sites sigue dando `true` en todos.
   *
   * Sin ella, el cliente arma su propia subclase con `name === errorName`.
   */
  readonly errorClass?: TypedApiErrorClass;
}

export interface TypedApiClient {
  /** Subclase de `TypedApiError` con `name === errorName`. Única por cliente: el `instanceof` discrimina. */
  readonly ApiError: TypedApiErrorClass;
  // Declarados como PROPIEDADES de tipo función (no method signatures) a
  // propósito: los wrappers los desestructuran (`const { get, send } = client`)
  // y ninguno usa `this`.
  /**
   * Transporte crudo: `basePath` + `content-type` + mapeo de red/abort.
   * **No mira `res.ok`** — es la pieza para los wrappers con desenvuelto propio
   * (blob, JSON crudo, `body.data` estricto).
   */
  readonly request: (path: string, init?: RequestInit) => Promise<Response>;
  /** Tira `ApiError` si la respuesta no es ok (leyendo `{ error }` del body). No toca el body si es ok. */
  readonly ensureOk: (res: Response) => Promise<void>;
  /** Lectura: `request` + `ensureOk` + desenvuelto del envelope `{ success, data }` (shape plano ⇒ tal cual). */
  readonly get: <T>(path: string, init?: RequestInit) => Promise<T>;
  /** Escritura: idéntico a `get`, con el `init` (method/body) del caller. Existe para que el call site se lea. */
  readonly send: <T>(path: string, init: RequestInit) => Promise<T>;
}

const NETWORK_MESSAGE = 'No pudimos conectar con el servidor.';

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

export function createTypedApiClient(opts: TypedApiClientOptions): TypedApiClient {
  const { basePath, errorName, abortError } = opts;

  // Subclase POR cliente (no una instancia compartida): dos clientes distintos
  // tienen que dar `false` en el `instanceof` cruzado, igual que las 11 clases
  // que reemplaza.
  const ApiError: TypedApiErrorClass =
    opts.errorClass ??
    class extends TypedApiError {
      constructor(
        message: string,
        status: number,
        code: string,
        details: Record<string, unknown> = {},
      ) {
        super(message, status, code, details);
        this.name = errorName;
      }
    };

  async function request(path: string, init: RequestInit = {}): Promise<Response> {
    try {
      // FormData setea su propio content-type (con boundary): no lo pisamos.
      const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
      return await apiFetch(`${basePath}${path}`, {
        ...init,
        headers: {
          ...(init.body && !isForm ? { 'content-type': 'application/json' } : {}),
          ...(init.headers ?? {}),
        },
      });
    } catch (err) {
      if (abortError === 'rethrow' && isAbort(err)) throw err;
      throw new ApiError(NETWORK_MESSAGE, 0, 'network_error');
    }
  }

  async function ensureOk(res: Response): Promise<void> {
    if (res.ok) return;
    // `res.json()` resuelve con CUALQUIER JSON válido, no sólo con un objeto:
    // el `.catch()` cubre el body no parseable (HTML de un proxy, cuerpo
    // vacío), pero no el `null` literal —ahí `body.error` tiraba un TypeError,
    // o sea el wrapper fallaba por la razón equivocada justo cuando el backend
    // ya estaba fallando—. Lo que no es un objeto no tiene ni `error` ni
    // detalles que subir, así que entra como `details: {}` (el mismo trato que
    // el body no parseable, ver el docblock de `TypedApiError`).
    const parsed: unknown = await res.json().catch(() => null);
    const body: { error?: string } & Record<string, unknown> =
      typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as { error?: string } & Record<string, unknown>)
        : {};
    throw new ApiError(
      body.error ?? `HTTP ${res.status}`,
      res.status,
      body.error ?? 'unknown',
      body,
    );
  }

  async function unwrap<T>(res: Response): Promise<T> {
    await ensureOk(res);
    const json = (await res.json()) as { data?: T } | T;
    if (json && typeof json === 'object' && 'data' in (json as Record<string, unknown>)) {
      return (json as { data?: T }).data as T;
    }
    return json as T;
  }

  return {
    ApiError,
    request,
    ensureOk,
    get: <T>(path: string, init: RequestInit = {}): Promise<T> =>
      request(path, init).then((res) => unwrap<T>(res)),
    send: <T>(path: string, init: RequestInit): Promise<T> =>
      request(path, init).then((res) => unwrap<T>(res)),
  };
}
